"use client"

import type { ApplicationFlowStep } from "@/modules/jobAdDetail/components/ApplicationFlow/ApplicationFlow"
import type { PageLayoutContextValue } from "@/modules/jobAdDetail/components/PageLayout/PageLayoutContext"
import type { JobAdDetail, Params, SearchParams } from "@/modules/jobAdDetail/types"
import type { TrackingContext } from "@/modules/jobAdDetail/utils/getTrackingContext"
import type { FunctionComponent, PropsWithChildren } from "react"

import classNames from "classnames"
import dynamic from "next/dynamic"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useState } from "react"

import { PageLayoutContext } from "@/modules/jobAdDetail/components/PageLayout/PageLayoutContext"
import { DOM_REF_IDS, LOCAL_STORAGE_APPLIED_EXTERNAL_JOB_ADS } from "@/modules/jobAdDetail/constants"
import { useJobAdDetailTracking } from "@/modules/jobAdDetail/hooks/useJobAdDetailTracking"
import { getSimilarJobAds } from "@/modules/jobAdDetail/services/getSimilarJobAds"
import { postJobAdSeenState } from "@/modules/jobAds/client/api/postJobAdSeenState"
import { Layout } from "@/modules/layouts/components/Layout/Layout"
import { logger } from "@/modules/monitoring/logger"
import { useHref } from "@/modules/routing/hooks/useHref"

import styles from "@/modules/jobAdDetail/components/PageLayout/PageLayout.module.css"

type InteractivePageContentProps = PropsWithChildren<{
  applicationFlowIsEnabled: boolean
  baseUrl: string
  careerCenterName: string
  candidacyMessageType: JobAdDetail["candidacyMessageType"]
  companyId: string
  hasApplied: boolean
  isApplicationFromDashboardEnabled: boolean
  isCandidacyInternal: boolean
  isNewSignInFlowEnabled: boolean
  isUserLogged: boolean
  isUserAuthorizedToApplyInternally: boolean
  isWwwCareerCenter: boolean
  jobAdId: JobAdDetail["id"]
  jobAdLocale: JobAdDetail["languageCode"]
  loginModalTitle: string
  trackingContext: TrackingContext
  userToken: string
  withCVOnlyFeature: boolean
}>

const DynamicQueryClientProviderWrapper = dynamic(() =>
  import("@/modules/queryClient/components/QueryClientProviderWrapper").then(mod => mod.QueryClientProviderWrapper)
)

const DynamicAppTranslationProvider = dynamic(() =>
  import("@/modules/i18n/components/AppTranslationProviderWrapper").then(mod => mod.AppTranslationProviderWrapper)
)

const DynamicLoginModal = dynamic(
  () => import("@/modules/authentication/components/LoginModal/LoginModal").then(mod => mod.LoginModal),
  { ssr: false }
)

const DynamicNewLoginModal = dynamic(
  () => import("@/modules/authentication/components/LoginModal/NewLoginModal").then(mod => mod.NewLoginModal),
  { ssr: false }
)

const DynamicAppReportFlowModal = dynamic(
  () => import("@/modules/jobAdDetail/components/ReportFlow/AppReportFlow").then(mod => mod.AppReportFlow),
  { ssr: false }
)

const DynamicPageLayoutAside = dynamic(() => import("./PageLayoutAside").then(mod => mod.PageLayoutAside), {
  ssr: false,
})

export const InteractivePageContent: FunctionComponent<InteractivePageContentProps> = ({
  applicationFlowIsEnabled,
  baseUrl,
  candidacyMessageType,
  careerCenterName,
  children,
  companyId,
  hasApplied,
  isApplicationFromDashboardEnabled,
  isCandidacyInternal,
  isNewSignInFlowEnabled,
  isUserAuthorizedToApplyInternally,
  isUserLogged,
  isWwwCareerCenter,
  jobAdId,
  jobAdLocale,
  loginModalTitle,
  trackingContext,
  userToken,
  withCVOnlyFeature,
}) => {
  const { refresh } = useRouter()
  const currentPageHref = useHref()
  const searchParams = useSearchParams()
  const { jobAdSlug } = useParams<Params>()
  const currentParamActionType = searchParams.get("actionType")
  const { trackEventClick, commonRefIdsProps } = useJobAdDetailTracking()

  const isCandidacyExternal = !isCandidacyInternal
  const [isApplicationFlowOpen, setIsApplicationFlowOpen] = useState(false)
  const [isApplicationFlowDisplayed, setIsApplicationFlowDisplayed] = useState(false)
  const [displaySimilarJobAds, setDisplaySimilarJobAds] = useState(false)

  useEffect(() => {
    /*
      A logged-in user who has already applied to the job offer
      and to whom we have similar jobs to offer
      should see them directly
    */
    ;(async () => {
      if (!isUserLogged) {
        return
      }

      if (isCandidacyInternal && !isUserAuthorizedToApplyInternally) {
        return
      }

      const hasUserApplied =
        (isCandidacyInternal && hasApplied) ||
        (isCandidacyExternal && window.localStorage.getItem(LOCAL_STORAGE_APPLIED_EXTERNAL_JOB_ADS)?.includes(jobAdId))

      if (!hasUserApplied) {
        return
      }

      const similarJobAds = await getSimilarJobAds({ jobAdSlug }).catch(error => {
        logger.error(error)
        return []
      })

      const hasSimilarJobAds = !!similarJobAds.length

      if (!hasSimilarJobAds) {
        return
      }
      document.body.classList.remove("app-Body--noScrollForMediumAndLessScreen")

      setDisplaySimilarJobAds(true)
      setIsApplicationFlowOpen(true)
      setIsApplicationFlowDisplayed(true)
    })()
  }, [
    hasApplied,
    isCandidacyExternal,
    isCandidacyInternal,
    isUserAuthorizedToApplyInternally,
    isUserLogged,
    jobAdId,
    jobAdSlug,
  ])

  const [isLoginFlowOpen, setIsLoginFlowOpen] = useState(false)
  const [displayReportFlow, setReportModalDisplay] = useState(false)
  const [redirectToUrl, setRedirectToUrl] = useState<string>("")

  const openLoginFlow = useCallback(
    (nextActionType: SearchParams["actionType"]): void => {
      const currentPageHrefFormatted = new URL(currentPageHref, baseUrl)

      if (nextActionType && isCandidacyInternal) {
        currentPageHrefFormatted.searchParams.append("actionType", nextActionType)
      }

      setRedirectToUrl(currentPageHrefFormatted.toString())
      setIsLoginFlowOpen(true)
    },
    [baseUrl, currentPageHref, isCandidacyInternal]
  )

  const openApplicationFlow = useCallback(
    (step?: ApplicationFlowStep) => {
      if (!isUserLogged) {
        openLoginFlow("openApplicationFlow")
        return
      }

      document.body.classList.add("app-Body--noScrollForMediumAndLessScreen")
      setIsApplicationFlowOpen(true)
      setIsApplicationFlowDisplayed(true)
      if (step === "similarJobAds") setDisplaySimilarJobAds(true)
    },
    [isUserLogged, openLoginFlow]
  )

  const closeApplicationFlow = useCallback((): void => {
    trackEventClick("job-ad-detail-apply-modal-close")
    setIsApplicationFlowOpen(false)

    document.body.classList.remove("app-Body--noScrollForMediumAndLessScreen")
  }, [trackEventClick])

  const removeApplicationFlow = useCallback((): void => {
    setIsApplicationFlowDisplayed(false)
  }, [])

  const closeLoginModal = useCallback((): void => {
    setIsLoginFlowOpen(false)
  }, [])

  const openReportFlow = useCallback((): void => {
    trackEventClick("job-ad-detail-report-modal-open")
    setReportModalDisplay(true)
  }, [trackEventClick])

  const closeReportFlow = useCallback((): void => {
    setReportModalDisplay(false)
  }, [])

  const printJobAd = useCallback((): void => {
    trackEventClick("job-ad-detail-print")
    window.print()
  }, [trackEventClick])

  const handleApplySuccess = (): void => {
    refresh()
  }

  useEffect(() => {
    if (isUserLogged) {
      postJobAdSeenState({ jobAdId }).catch(error => {
        logger.error(error)
      })
    }
  }, [isUserLogged, jobAdId])

  useEffect(() => {
    // Open application flow when user is logged and he has been redirected to jobad after login
    if (currentParamActionType === "openApplicationFlow" && applicationFlowIsEnabled && isUserLogged) {
      openApplicationFlow()
    }
    // We only want to perform this check during the initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contextValue: PageLayoutContextValue = useMemo(
    () => ({
      closeApplicationFlow,
      closeLoginModal,
      closeReportFlow,
      openApplicationFlow,
      openLoginFlow,
      openReportFlow,
      printJobAd,
      removeApplicationFlow,
      trackingContext,
    }),
    [
      closeApplicationFlow,
      closeLoginModal,
      closeReportFlow,
      openApplicationFlow,
      openLoginFlow,
      openReportFlow,
      printJobAd,
      removeApplicationFlow,
      trackingContext,
    ]
  )

  return (
    <PageLayoutContext.Provider value={contextValue}>
      {/* Login Flow */}
      {isLoginFlowOpen && isNewSignInFlowEnabled && (
        <DynamicAppTranslationProvider>
          <DynamicQueryClientProviderWrapper>
            <DynamicNewLoginModal
              careerCenterName={careerCenterName}
              isWwwCareerCenter={isWwwCareerCenter}
              onClose={closeLoginModal}
              trackingRefIdsProps={commonRefIdsProps}
              urlRedirectionAfterLogin={redirectToUrl}
            />
          </DynamicQueryClientProviderWrapper>
        </DynamicAppTranslationProvider>
      )}
      {isLoginFlowOpen && !isNewSignInFlowEnabled && (
        <DynamicAppTranslationProvider>
          <DynamicQueryClientProviderWrapper>
            <DynamicLoginModal
              careerCenterName={careerCenterName}
              isWwwCareerCenter={isWwwCareerCenter}
              onClose={closeLoginModal}
              title={loginModalTitle}
              trackingRefIdsProps={commonRefIdsProps}
              urlRedirectionAfterLogin={redirectToUrl}
            />
          </DynamicQueryClientProviderWrapper>
        </DynamicAppTranslationProvider>
      )}

      {/* Report Flow */}
      {displayReportFlow && (
        <DynamicAppReportFlowModal
          jobAdId={jobAdId}
          onClose={() => {
            closeReportFlow()
          }}
          onError={() => {
            closeReportFlow()
          }}
        />
      )}

      <div className={styles.wrapper}>
        {/* Application Flow */}
        {isApplicationFlowDisplayed && (
          <DynamicAppTranslationProvider>
            <DynamicQueryClientProviderWrapper>
              <DynamicPageLayoutAside
                candidacyMessageType={candidacyMessageType}
                companyId={companyId}
                isApplicationFromDashboardEnabled={isApplicationFromDashboardEnabled}
                isCandidacyInternal={isCandidacyInternal}
                isUserAuthorizedToApplyInternally={isUserAuthorizedToApplyInternally}
                jobAdId={jobAdId}
                jobAdLocale={jobAdLocale}
                onSuccess={handleApplySuccess}
                userToken={userToken}
                displaySimilarJobAds={displaySimilarJobAds}
                withCVOnlyFeature={withCVOnlyFeature}
              />
            </DynamicQueryClientProviderWrapper>
          </DynamicAppTranslationProvider>
        )}

        <Layout
          as="main"
          className={classNames(styles.contentLayout, { [styles.contentLayout___withAside]: isApplicationFlowOpen })}
          id={DOM_REF_IDS.CONTENT}
        >
          {children}
        </Layout>
      </div>
    </PageLayoutContext.Provider>
  )
}
